import type {
    GetLeadHistoryParams,
    LeadHistoryList,
    LeadHistoryRow,
} from "../../../helpers/leads/reports";

const DAY_MS = 24 * 60 * 60 * 1000;
const MOCK_COUNT = 24;

const RETRYABLE_STATUSES = [
    "SENT",
    "PROCESS_FAILED",
    "SEND_FAILED",
    "FAILED_TO_FETCH_DATA",
];

const STATUS_CYCLE = [
    "SENT",
    "PROCESS_FAILED",
    "SEND_FAILED",
    "FAILED_TO_FETCH_DATA",
    "DUPLICATED",
    "RECEIVED",
    "PROCESSED",
];

const retriedStatIds = new Set<number>();
/** Frozen so a refetch after retry does not shift dates or row order. */
const MOCK_NOW = Date.now();

const toApiDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day} ${hours}:${minutes}:00`;
};

const buildRow = (index: number, now: number): LeadHistoryRow => {
    const status = STATUS_CYCLE[index % STATUS_CYCLE.length];
    const statId = 880000 + index;
    const scheduled = index === 1 || retriedStatIds.has(statId);
    const retryable = RETRYABLE_STATUSES.includes(status);

    return {
        stat_id: statId,
        lead_id: `LD-${statId}`,
        date: toApiDate(new Date(now - index * DAY_MS)),
        status,
        scheduled,
        retry_available: retryable && !scheduled,
        last_response: retriedStatIds.has(statId)
            ? {
                  message:
                      "Mock: retry scheduled. The next result will replace this response.",
              }
            : {
                  response: {
                      code: status === "SENT" ? 201 : 422,
                      message:
                          index === 4
                              ? "Mock: destination rejected the payload because `email` is not a valid address and `phone` must be E.164."
                              : `Mock: ${status} response for lead ${statId}.`,
                  },
              },
    };
};

const buildRows = (): LeadHistoryRow[] =>
    Array.from({ length: MOCK_COUNT }, (_, index) =>
        buildRow(index, MOCK_NOW),
    );

/**
 * True after a DEV_MODE retry for this stat, including retries started from
 * the debug page. Debug fixtures read this so a refetch shows Scheduled.
 */
export const isDevMockLeadRetried = (statId: number): boolean =>
    retriedStatIds.has(statId);

/**
 * Marks retryable rows as scheduled. Returns an error when every requested
 * row is duplicated or already scheduled, and leaves those rows unchanged.
 */
export const applyDevMockRetry = (statIds: number[]): string | null => {
    const rows = buildRows();
    const targets = rows.filter((row) => statIds.includes(row.stat_id));
    const retryable = targets.filter(
        (row) => row.retry_available && !row.scheduled,
    );

    if (!retryable.length) {
        return "This lead cannot be retried.";
    }

    retryable.forEach((row) => retriedStatIds.add(row.stat_id));
    return null;
};

export const buildDevMockLeadHistory = (
    params: GetLeadHistoryParams = {},
): LeadHistoryList => {
    const pageSize = Math.min(params.page_size ?? 10, 100);
    const statuses = (params.status ?? "")
        .split(",")
        .map((status) => status.trim())
        .filter(Boolean);
    const filtered = buildRows().filter((row) => {
        if (statuses.length && !statuses.includes(row.status)) return false;
        if (params.start_date && params.end_date) {
            const day = row.date.slice(0, 10);
            if (day < params.start_date || day > params.end_date) return false;
        }
        return true;
    });

    const lastPage = Math.max(1, Math.ceil(filtered.length / pageSize));
    const currentPage = Math.min(Math.max(1, params.page ?? 1), lastPage);
    const offset = (currentPage - 1) * pageSize;

    return {
        leads: filtered.slice(offset, offset + pageSize),
        pagination: {
            current_page: currentPage,
            per_page: pageSize,
            total: filtered.length,
            last_page: lastPage,
        },
    };
};
